import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

type SubscriptionModalMode = 'subscribe' | 'contact' | null;

interface SubscriptionModalContextType {
  isOpen: boolean;
  mode: SubscriptionModalMode;
  selectedTier: string | null;
  openSubscription: (tier?: string) => void;
  openContactMode: (tier?: string) => void;
  closeModal: () => void;
}

const SubscriptionModalContext = createContext<SubscriptionModalContextType | undefined>(undefined);

interface SubscriptionModalProviderProps {
  children: ReactNode;
}

export const SubscriptionModalProvider: React.FC<SubscriptionModalProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<SubscriptionModalMode>(null);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);

  const DISMISSED_KEY = 'subscription_modal_dismissed';

  // Auto-open for users requiring subscription (unless dismissed this session)
  useEffect(() => {
    if (user?.requiresSubscription && !user.isLicenseUser) {
      // Don't auto-open if user is in the signup wizard flow
      if (window.location.pathname.startsWith('/signup')) return;
      const dismissed = sessionStorage.getItem(DISMISSED_KEY);
      if (!dismissed) {
        openSubscription();
      }
    }
  }, [user?.requiresSubscription, user?.isLicenseUser]);

  // Handle URL-based modal triggers and Stripe cancellation returns
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const modalParam = params.get('modal');
    const modeParam = params.get('mode');
    const tierParam = params.get('tier');

    // Only process subscription modal URL params
    if (modalParam !== 'subscription' || modeParam !== 'subscribe') {
      return;
    }

    // Wait for user to be loaded before processing
    if (!user) {
      return;
    }

    // License users (customerTypeId 2 or 3) cannot access subscription modal
    // Clear the URL params and redirect to dashboard
    if (user.isLicenseUser || user.customerTypeId === 2 || user.customerTypeId === 3) {
      // Clear subscription-related URL params
      params.delete('modal');
      params.delete('mode');
      params.delete('tier');
      params.delete('cancelled');
      const newSearch = params.toString();
      window.history.replaceState(
        {},
        '',
        newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname
      );
      return;
    }

    openSubscription(tierParam || undefined);
  }, [user]);

  const openSubscription = (tier?: string) => {
    // Prevent license users from opening the modal
    if (user?.isLicenseUser || user?.customerTypeId === 2 || user?.customerTypeId === 3) {
      return;
    }

    setMode('subscribe');
    setSelectedTier(tier || null);
    setIsOpen(true);
    updateURL('subscribe', tier);
  };

  const openContactMode = (tier?: string) => {
    // Prevent license users from opening the modal
    if (user?.isLicenseUser || user?.customerTypeId === 2 || user?.customerTypeId === 3) {
      return;
    }

    setMode('contact');
    setSelectedTier(tier || null);
    setIsOpen(true);
    updateURL('contact', tier);
  };

  const closeModal = () => {
    setIsOpen(false);
    setMode(null);
    setSelectedTier(null);
    clearURL();

    // Remember dismissal for the session so it doesn't auto-reopen
    if (user?.requiresSubscription) {
      sessionStorage.setItem(DISMISSED_KEY, 'true');
    }
  };

  const updateURL = (newMode: string, tier?: string | null) => {
    const params = new URLSearchParams(window.location.search);
    params.set('modal', 'subscription');
    params.set('mode', newMode);
    if (tier) {
      params.set('tier', tier);
    } else {
      params.delete('tier');
    }
    // Remove cancelled flag if present
    params.delete('cancelled');

    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  const clearURL = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('modal');
    params.delete('mode');
    params.delete('tier');
    params.delete('cancelled');

    const newSearch = params.toString();
    window.history.replaceState(
      {},
      '',
      newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname
    );
  };

  const value: SubscriptionModalContextType = {
    isOpen,
    mode,
    selectedTier,
    openSubscription,
    openContactMode,
    closeModal,
  };

  return (
    <SubscriptionModalContext.Provider value={value}>
      {children}
    </SubscriptionModalContext.Provider>
  );
};

export const useSubscriptionModal = (): SubscriptionModalContextType => {
  const context = useContext(SubscriptionModalContext);
  if (!context) {
    throw new Error('useSubscriptionModal must be used within SubscriptionModalProvider');
  }
  return context;
};
